import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ToggleLeft, ToggleRight } from 'lucide-react';
import { Can } from '@/components/Can';
import { useToast } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
export function ModulesPage() {
    const qc = useQueryClient();
    const toast = useToast();
    const activeId = useAuthStore((s) => s.activeCompanyId);
    const catalog = useQuery({
        queryKey: ['module-catalog', activeId],
        queryFn: async () => (await api.get(`/tenants/companies/${activeId}/modules/`)).data,
        enabled: Boolean(activeId),
    });
    // Pull the company's current override arrays so we can mutate them.
    const company = useQuery({
        queryKey: ['company-detail', activeId],
        queryFn: async () => (await api.get(`/tenants/companies/${activeId}/`)).data,
        enabled: Boolean(activeId),
    });
    const save = useMutation({
        mutationFn: async ({ enabled, disabled }) => (await api.patch(`/tenants/companies/${activeId}/`, {
            module_overrides_enable: enabled,
            module_overrides_disable: disabled,
        })).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['module-catalog'] });
            qc.invalidateQueries({ queryKey: ['company-detail'] });
            qc.invalidateQueries({ queryKey: ['dashboard'] });
            qc.invalidateQueries({ queryKey: ['auth', 'me', 'companies'] });
            toast.success('Modules updated', 'Refresh the page to update the sidebar.');
        },
        onError: (err) => {
            const d = err.response?.data?.error ?? err.response?.data;
            toast.error('Could not update modules', typeof d === 'string' ? d : (d?.message || d?.detail || JSON.stringify(d?.details ?? d)));
        },
    });
    // Toggle: each module is either ON or OFF as visible to the user. The
    // saved state is split across enable/disable arrays so the industry
    // default can be overridden in either direction.
    function toggle(m, newVisible) {
        const enabled = new Set(company.data?.module_overrides_enable ?? []);
        const disabled = new Set(company.data?.module_overrides_disable ?? []);
        if (newVisible) {
            // Want it on. If default is on, no override needed; if default is off,
            // record an explicit enable.
            enabled.delete(m.key);
            disabled.delete(m.key);
            if (!m.default_on)
                enabled.add(m.key);
        }
        else {
            // Want it off. If default is off, no override needed; if default is on,
            // record an explicit disable.
            enabled.delete(m.key);
            disabled.delete(m.key);
            if (m.default_on)
                disabled.add(m.key);
        }
        save.mutate({ enabled: Array.from(enabled), disabled: Array.from(disabled) });
    }
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("header", { children: [_jsx("h1", { className: "text-2xl font-semibold", children: "Modules" }), _jsx("p", { className: "text-sm text-muted-foreground", children: "Industry-specific verticals are hidden by default. Turn them on for this company here \u2014 e.g., a software house that also sells physical merch can flip Inventory on. Common modules (accounting, sales, purchases, HR, banking, \u2026) are always available." }), catalog.data?.industry && (_jsxs("p", { className: "mt-2 text-xs text-muted-foreground", children: ["Industry: ", _jsx("code", { className: "font-mono", children: catalog.data.industry }), catalog.data.services_subtype && _jsxs(_Fragment, { children: [" \u00B7 subtype ", _jsx("code", { className: "font-mono", children: catalog.data.services_subtype })] })] }))] }), _jsx(Can, { code: "tenants.company.update", children: _jsx("ul", { className: "divide-y rounded-md border bg-card", children: catalog.data?.modules.map((m) => {
                        const visible = m.currently_visible;
                        return (_jsxs("li", { className: "flex items-center justify-between gap-4 px-4 py-3", children: [_jsxs("div", { children: [_jsx("div", { className: "text-sm font-medium", children: m.label }), _jsxs("div", { className: "text-xs text-muted-foreground", children: ["Default for this industry:", ' ', _jsx("span", { className: m.default_on ? 'text-emerald-700' : 'text-amber-700', children: m.default_on ? 'on' : 'off' }), m.force_enabled && _jsxs(_Fragment, { children: [" \u00B7 ", _jsx("span", { className: "text-emerald-700", children: "force-enabled" })] }), m.force_disabled && _jsxs(_Fragment, { children: [" \u00B7 ", _jsx("span", { className: "text-amber-700", children: "force-disabled" })] })] })] }), _jsx("button", { type: "button", onClick: () => toggle(m, !visible), disabled: save.isPending, className: `inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${visible
                                        ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                        : 'bg-muted text-muted-foreground hover:bg-muted/80'}`, "aria-pressed": visible, children: visible
                                        ? _jsxs(_Fragment, { children: [_jsx(ToggleRight, { className: "h-4 w-4" }), " On"] })
                                        : _jsxs(_Fragment, { children: [_jsx(ToggleLeft, { className: "h-4 w-4" }), " Off"] }) })] }, m.key));
                    }) }) })] }));
}
